import React, { useEffect, useState } from 'react';
import { X, Move, ZoomIn, MousePointer2 } from 'lucide-react';

const KEY = 'pm_touch_coachmark_seen_v1';

/**
 * First-time touch coach mark: shown only on touch devices, once ever.
 * Explains the three key gestures in the editor.
 */
export default function TouchCoachMark({ location = 'editor' }) {
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (!isTouch) return;
    try {
      if (localStorage.getItem(KEY)) return;
    } catch { /* ignore */ }
    setVisible(true);
  }, []);

  if (!visible) return null;

  const steps = [
    { icon: <ZoomIn className="w-8 h-8 text-[#06B6D4]" />, title: 'Pinch to zoom', body: 'Use two fingers to zoom in and out of the scene.' },
    { icon: <Move className="w-8 h-8 text-[#06B6D4]" />, title: 'Drag to orbit', body: 'One-finger drag on empty canvas orbits the camera around the scene.' },
    { icon: <MousePointer2 className="w-8 h-8 text-[#06B6D4]" />, title: 'Tap objects', body: 'Tap an object to select it, then tap the on-screen gizmo handles to move it.' },
  ];

  const dismiss = () => {
    try { localStorage.setItem(KEY, '1'); } catch { /* ignore */ }
    setVisible(false);
  };

  const s = steps[step];

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center p-4"
      data-testid={`touch-coachmark-${location}`}
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-sm bg-[#0A0A0B] border border-[#1E1E24] rounded-2xl p-6 shadow-2xl">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-2xl bg-[#06B6D4]/15 border border-[#06B6D4]/30 flex items-center justify-center shrink-0">
            {s.icon}
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-white mb-1">{s.title}</h3>
            <p className="text-sm text-zinc-400">{s.body}</p>
          </div>
          <button onClick={dismiss} className="text-zinc-500 hover:text-white" aria-label="Skip">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center justify-between mt-6">
          <div className="flex items-center gap-1.5">
            {steps.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${i === step ? 'w-6 bg-[#06B6D4]' : 'w-1.5 bg-white/20'}`}
              />
            ))}
          </div>
          {step < steps.length - 1 ? (
            <button
              onClick={() => setStep(step + 1)}
              className="text-sm font-semibold text-[#06B6D4] hover:text-[#06B6D4]/80"
              data-testid="touch-coachmark-next"
            >
              Next →
            </button>
          ) : (
            <button
              onClick={dismiss}
              className="text-sm font-semibold bg-[#06B6D4] text-black px-4 py-1.5 rounded-full hover:bg-[#06B6D4]/90"
              data-testid="touch-coachmark-done"
            >
              Got it
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
